import CardBody from '@/components/ui/cards/CardBody';
import CardHeader from '@/components/ui/cards/CardHeader';
import { Input } from '@/components/ui/inputs/input';
import { InputSelect } from '@/components/ui/inputs/input-select';
import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import validationErrors from '@/lib/validationErrors';
import {
    getPaymentBySlugService,
    testRaamsConnectionService,
    updatePaymentService,
} from '@/services/PaymentService';
import PermissionCheck from '@/components/common/PermissionCheck';

const modeOptions = [
    { value: '1', label: 'Live' },
    { value: '0', label: 'Sandbox' },
];

const statusOptions = [
    { value: 1, label: 'Enable' },
    { value: 2, label: 'Disable' },
];

const RaamsPayTab = () => {
    const {
        register,
        control,
        handleSubmit,
        setValue,
        getValues,
        reset,
        formState: { errors },
    } = useForm({
        defaultValues: {
            raams_id: '',
            raams_email: '',
            raams_password: '',
            raams_mode: '1',
            status: 1,
        },
    });
    const dispatch = useDispatch();
    const [isSubmitting, setSubmitting] = useState(false);
    const [isTesting, setTesting] = useState(false);
    const [gatewayId, setGatewayId] = useState('');
    const [testResult, setTestResult] = useState(null);

    useEffect(() => {
        dispatch(getPaymentBySlugService('raams'))
            .then((res) => {
                const gw = res?.data?.data?.[0];
                const opts = gw?.gateway_options ?? [];
                const currentGatewayId = gw?._id || '';
                setGatewayId(currentGatewayId);
                reset({
                    raams_id: currentGatewayId,
                    status: gw?.status ?? 1,
                    raams_email: opts.find((o) => o.option === 'raams_email')?.value ?? '',
                    raams_password: '',
                    raams_mode: String(opts.find((o) => o.option === 'raams_mode')?.value ?? '1'),
                });
            })
            .catch(({ message, errorData }) => validationErrors(errorData, message));
    }, [dispatch, reset]);

    const onSubmit = (data) => {
        setSubmitting(true);
        const resolvedGatewayId = String(data.raams_id || gatewayId || '').trim();
        dispatch(updatePaymentService({
            id: resolvedGatewayId,
            slug: 'raams',
            status: data.status,
            options: [
                { option: 'raams_email', value: data.raams_email },
                { option: 'raams_mode', value: data.raams_mode },
                ...(String(data.raams_password || '').trim()
                    ? [{ option: 'raams_password', value: data.raams_password }]
                    : []),
            ],
        }))
            .then((res) => {
                const savedGatewayId = res?.data?.data?._id || resolvedGatewayId;
                if (savedGatewayId) {
                    setGatewayId(savedGatewayId);
                    setValue('raams_id', savedGatewayId);
                }
            })
            .catch(({ message, errorData }) => validationErrors(errorData, message))
            .finally(() => setSubmitting(false));
    };

    const handleTestConnection = () => {
        const data = getValues();
        setTesting(true);
        setTestResult(null);

        dispatch(testRaamsConnectionService({
            raams_email: data.raams_email,
            raams_password: data.raams_password,
            raams_mode: data.raams_mode,
        }))
            .then((res) => {
                setTestResult(res?.data?.data || null);
            })
            .catch(({ message, errorData }) => {
                validationErrors(errorData, message);
                setTestResult(null);
            })
            .finally(() => setTesting(false));
    };

    return (
        <>
            <CardHeader isBorder>
                <h3 className="db-card-title">Raams</h3>
            </CardHeader>
            <CardBody>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <input type="hidden" {...register('raams_id')} />
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <Controller
                            control={control}
                            name="raams_email"
                            rules={{ required: true }}
                            render={({ field }) => (
                                <Input {...field} labelText="Payment Username" labelStyleClassName="db-field-title required" errorType={errors.raams_email?.type} size="sm" />
                            )}
                        />
                        <Controller
                            control={control}
                            name="raams_password"
                            render={({ field }) => (
                                <Input
                                    {...field}
                                    type="password"
                                    labelText="Payment Password"
                                    labelStyleClassName="db-field-title"
                                    errorType={errors.raams_password?.type}
                                    size="sm"
                                    placeholder="Leave blank to keep current password"
                                />
                            )}
                        />
                        <Controller
                            control={control}
                            name="raams_mode"
                            rules={{ required: true }}
                            render={({ field }) => (
                                <InputSelect
                                    {...field}
                                    labelText="Mode"
                                    labelStyleClassName="db-field-title required"
                                    options={modeOptions}
                                    value={modeOptions.find((o) => o.value === String(field.value)) || null}
                                    onChange={(opt) => field.onChange(opt.value)}
                                    errorType={errors.raams_mode?.type}
                                />
                            )}
                        />
                        <Controller
                            control={control}
                            name="status"
                            rules={{ required: true }}
                            render={({ field }) => (
                                <InputSelect
                                    {...field}
                                    labelText="Status"
                                    labelStyleClassName="db-field-title required"
                                    options={statusOptions}
                                    value={statusOptions.find((o) => o.value === field.value) || null}
                                    onChange={(opt) => field.onChange(opt.value)}
                                    errorType={errors.status?.type}
                                />
                            )}
                        />
                        <div className="sm:col-span-2 text-xs text-gray-500">
                            Username, password, mode, and status match the Taban website setup.
                            Connection URLs and API paths are kept hidden on the backend.
                        </div>
                        <PermissionCheck permsission="update_settings">
                            <div className="sm:col-span-2 flex flex-wrap items-center gap-3">
                                <button type="submit" disabled={isSubmitting} className="db-btn text-white bg-primary">
                                    <i className="lab lab-save" />
                                    <span>Save</span>
                                </button>
                                <button
                                    type="button"
                                    disabled={isTesting}
                                    onClick={handleTestConnection}
                                    className="db-btn bg-white border border-gray-200 text-gray-700"
                                >
                                    <i className="lab lab-link" />
                                    <span>{isTesting ? 'Testing...' : 'Test Connection'}</span>
                                </button>
                                <span className="text-xs text-gray-500">
                                    If the password field is blank, the test uses the saved password.
                                </span>
                            </div>
                        </PermissionCheck>
                        {testResult?.connected ? (
                            <div className="sm:col-span-2 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
                                <p className="font-medium">
                                    Connected in {testResult.mode === 'live' ? 'Live' : 'Sandbox'} mode.
                                    {` ${testResult.gatewayCount} gateway${testResult.gatewayCount === 1 ? '' : 's'} returned.`}
                                </p>
                                {Array.isArray(testResult.gateways) && testResult.gateways.length ? (
                                    <p className="mt-2 text-xs text-emerald-700">
                                        {testResult.gateways
                                            .map((gateway) => `${gateway.name} (${gateway.id})`)
                                            .join(', ')}
                                    </p>
                                ) : null}
                            </div>
                        ) : null}
                    </div>
                </form>
            </CardBody>
        </>
    );
};

export default RaamsPayTab;
